import React, { useMemo, useState } from 'react';

import { Radio, Skeleton, Table, Tabs, Tooltip } from 'antd';

import Chart from 'components/Chart';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useBestsellers } from 'v2/hooks/useBestsellers';

import chartColors from '../../chartOptions';

type DoughnutChartProps = {
  id: string;
};

type DoughnutDataset = {
  label: string;
  backgroundColor: string[];
  data: number[];
  values: number[];
  fill: 'start' | 'end' | boolean;
};

type DoughnutChartData = {
  labels: string[];
  datasets: DoughnutDataset[];
};

type Period = '7' | '30' | 'all';

enum TabKeys {
  PRODUCER = '0',
  COPRODUCER = '1',
  AFFILIATE = '2'
}

const paletteColors = [
  chartColors.red.background,
  chartColors.orange.background,
  chartColors.yellow.background,
  chartColors.green.background,
  chartColors.blue.background,
  chartColors.purple.background,
  chartColors.grey.background
];

const chartOptions = {
  responsive: true,
  legend: {
    display: false
  }
};

function getBackgroundColors(length: number) {
  return Array.from({ length }, (_: unknown, index: number) => paletteColors[index % paletteColors.length]);
}

const DoughnutChart = ({ id }: DoughnutChartProps) => {
  const [tab, setTab] = useState<string>(TabKeys.PRODUCER);
  const [period, setPeriod] = useState<Period>('7');

  const { bestsellers, isLoading } = useBestsellers(period);

  const source = useMemo(() => {
    switch (tab) {
      case TabKeys.COPRODUCER:
        return bestsellers?.coproducer;
      case TabKeys.AFFILIATE:
        return bestsellers?.affiliate;
      default:
        return bestsellers?.producer;
    }
  }, [tab, bestsellers]);

  const tabLabel = useMemo(() => {
    switch (tab) {
      case TabKeys.COPRODUCER:
        return 'Coprodutor';
      case TabKeys.AFFILIATE:
        return 'Afiliado';
      default:
        return 'Produtor';
    }
  }, [tab]);

  const labels: string[] = useMemo(() => source?.labels ?? [], [source]);
  const quantities: number[] = useMemo(() => source?.data ?? [], [source]);
  const values: number[] = useMemo(() => source?.values ?? [], [source]);
  const backgroundColors = useMemo(
    () => getBackgroundColors(quantities.length || labels.length),
    [labels.length, quantities.length]
  );

  const graphData: DoughnutChartData | null = useMemo(() => {
    if (!labels.length) return null;
    return {
      labels,
      datasets: [
        {
          label: tabLabel,
          backgroundColor: backgroundColors,
          data: quantities,
          values,
          fill: 'start'
        }
      ]
    };
  }, [labels, quantities, values, backgroundColors, tabLabel]);

  const totalPeriod = useMemo(() => values.reduce((sum: number, v: number) => sum + (v || 0), 0), [values]);

  const hasData = !!graphData?.labels?.length;

  const onChangePeriod = async (e: any) => {
    const newPeriod = e.target.value;
    if (!newPeriod) return;
    setPeriod(newPeriod);
  };

  const tableColumns = [
    {
      title: 'Conteúdo',
      key: 'content',
      render: (_: any, record: any) => (
        <div
          style={{
            borderLeft: '5px solid ' + record.color,
            paddingLeft: 8
          }}
        >
          <Tooltip title={record.label.length > 30 ? record.label : ''} placement='top'>
            <span>
              <Truncate count={30} final='...'>
                {record.label}
              </Truncate>
            </span>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Quantidade',
      key: 'quantity',
      align: 'right' as const,
      render: (_: any, record: any) => <Mask>{record.quantity}</Mask>
    },
    {
      title: 'Ganho',
      key: 'value',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <Mask>
          <ColoredFormattedNumber value={record.value} />
        </Mask>
      )
    }
  ];

  const tableData = graphData
    ? graphData.labels.map((item: string, i: number) => ({
        key: item,
        label: item,
        color: graphData.datasets[0].backgroundColor[i],
        quantity: graphData.datasets[0].data[i],
        value: graphData.datasets[0].values[i]
      }))
    : [];

  return (
    <div className='h-full rounded-sm bg-white shadow-sm'>
      <div className='h-full min-h-[430px]'>
        <div>
          <div className='flex flex-wrap items-center justify-between gap-2 p-4'>
            <div>
              <h3 className='m-0 text-xl font-medium'>Conteúdos mais vendidos</h3>
              <span className='text-sm text-gray-500'>
                Total no período:{' '}
                {isLoading ? (
                  <Skeleton.Input active size='small' style={{ width: 80 }} />
                ) : (
                  <Mask>
                    <ColoredFormattedNumber value={totalPeriod} />
                  </Mask>
                )}
              </span>
            </div>
            <Radio.Group size='small' value={period} onChange={onChangePeriod}>
              <Radio.Button className='min-w-[40px]' value='7'>
                7 dias
              </Radio.Button>
              <Radio.Button className='min-w-[40px]' value='30'>
                30 dias
              </Radio.Button>
              <Radio.Button className='min-w-[40px]' value='all'>
                Todos
              </Radio.Button>
            </Radio.Group>
          </div>

          <Tabs
            activeKey={tab}
            onChange={key => setTab(key)}
            centered
            items={[
              { key: TabKeys.PRODUCER, label: 'Produtor', disabled: isLoading },
              { key: TabKeys.COPRODUCER, label: 'Coprodutor', disabled: isLoading },
              { key: TabKeys.AFFILIATE, label: 'Afiliado', disabled: isLoading }
            ]}
          />

          <div>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 430 }}>
                <Spin />
              </div>
            ) : hasData ? (
              <div>
                <div className='h-[220px] pt-6'>
                  <Chart id={id} type='doughnut' options={chartOptions} data={graphData!} />
                </div>
                <Table columns={tableColumns} dataSource={tableData} pagination={false} size='small' />
              </div>
            ) : (
              <div style={{ textAlign: 'center', paddingTop: 16 }}>Não há dados a serem mostrados</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoughnutChart;
